import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, urls } from './config.dev';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Topbar from './Topbar';

// ─── MOBILE DETECTION HOOK ────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth <= 768
    );
  });

  useEffect(() => {
    const check = () => {
      setIsMobile(
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth <= 768
      );
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
};

const Sales = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(true);
  const [employeeName, setEmployeeName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDate, setShiftDate] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [comments, setComments] = useState('');
  const [newSale, setNewSale] = useState({ amount: 0, reason: '', servedBy: '', category: '' });
  const [newExpense, setNewExpense] = useState({ amount: 0, details: '', takenBy: '', servedBy: '', category: '' });
  const [totalSales, setTotalSales] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [cashAtHand, setCashAtHand] = useState(0);
  const [isAddingSale, setIsAddingSale] = useState(false);
  const [isAddingExpense, setIsAddingExpense] = useState(false);
  const [isClosingShift, setIsClosingShift] = useState(false);
  const [employee, setEmployee] = useState('');
  const [deletingSaleId, setDeletingSaleId] = useState(null);
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);
  const [hasSalesHistoryAccess, setHasSalesHistoryAccess] = useState(false);
  const [activeView, setActiveView] = useState('sales');
  const [showCloseShiftPrompt, setShowCloseShiftPrompt] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('blue');
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [hoveredNavItem, setHoveredNavItem] = React.useState(null);
  // Mobile-specific UI states
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileAddSale, setShowMobileAddSale] = useState(false);
  const [showMobileAddExpense, setShowMobileAddExpense] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Filter states
  const [filterType, setFilterType] = useState('servedBy');
  const [selectedServedByFilters, setSelectedServedByFilters] = useState([]);
  const [selectedCategoryFilters, setSelectedCategoryFilters] = useState([]);
  const [availableServedBy, setAvailableServedBy] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [expenseFilterType, setExpenseFilterType] = useState('takenBy');
  const [selectedExpenseTakenByFilters, setSelectedExpenseTakenByFilters] = useState([]);
  const [selectedExpenseCategoryFilters, setSelectedExpenseCategoryFilters] = useState([]);
  const [availableExpenseTakenBy, setAvailableExpenseTakenBy] = useState([]);
  const [availableExpenseCategories, setAvailableExpenseCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [fetchedSalesCategories, setFetchedSalesCategories] = useState([]);

  // Animation states
  const [animationProgress, setAnimationProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);

  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get('token');
  const [formattedDate, setFormattedDate] = useState('');

  const messages = [
    "Calculating employee performances...",
    "Awarding employee points...",
    "Setting patient appointments...",
    "Sending feedback requests to patients...",
    "Writing SMS for Upcoming patient Birthdays ...",
    "Setting family planning return dates...",
    "Collecting sales data...",
    "Calculating total expenses...",
    "Collecting shift comments...",
    "SMSing the shift report to Administrator...",
    "Thank you, goodbye..."
  ];

  const hardcodedSalesCategories = [
    'treatment', 'maternity', 'radiology', 'lab',
    'Pharmacy Sales', 'Consultation', 'major surgery',
    'minor surgery', 'dental', 'non-categorized'
  ];

  // ─── On mobile, always use white theme ──────────────────────────────────────
  const effectiveTheme = isMobile ? 'white' : currentTheme;

  // ─── Set theme color for browser on mobile (white status bar) ────────────────
  useEffect(() => {
    if (isMobile) {
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', '#ffffff');

      let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!metaStatusBar) {
        metaStatusBar = document.createElement('meta');
        metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
        document.head.appendChild(metaStatusBar);
      }
      metaStatusBar.setAttribute('content', 'black-translucent');

      // Ensure viewport meta is set for mobile
      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement('meta');
        metaViewport.setAttribute('name', 'viewport');
        document.head.appendChild(metaViewport);
      }
      metaViewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }
  }, [isMobile]);

  // ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
  const colors = {
    blue: {
      sidebarBg: '#0a1e4a',
      sidebarBorder: '#1e3a8a',
      activeNavBg: '#2563eb',
      activeNavText: '#ffffff',
      inactiveNavText: '#e0e7ff',
      navHoverBg: '#1e3a8a',
      sectionHeaderText: '#94a3b8',
      mainBg: '#f8fafc',
      cardBg: '#ffffff',
      cardBorder: '#e2e8f0',
      accent: '#16a34a',
      accentLight: '#dcfce7',
      danger: '#dc2626',
      dangerLight: '#fef2f2',
      warning: '#d97706',
      info: '#2563eb',
      infoLight: '#eff6ff',
      textPrimary: '#0f172a',
      textSecondary: '#475569',
      textMuted: '#64748b',
      headerBg: '#ffffff',
      tableHeader: '#f1f5f9',
      tableRowHover: '#f8fafc',
      tableBorder: '#e2e8f0',
      badgeGreen: { bg: '#dcfce7', text: '#166534' },
      badgeRed: { bg: '#fee2e2', text: '#991b1b' },
      badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
      badgeGray: { bg: '#f1f5f9', text: '#475569' },
      modalOverlay: 'rgba(0, 0, 0, 0.5)',
      modalBg: '#ffffff',
      pureWhite: '#ffffff',
      iconBright: '#fbbf24',
      collapseButtonBg: '#1e3a8a',
      collapseButtonHover: '#2563eb',
      collapseButtonText: '#ffffff',
      tooltipBg: '#1e293b',
      tooltipText: '#ffffff',
      filterSection: '#0d2257',
    },
    white: {
      sidebarBg: '#ffffff',
      sidebarBorder: '#e2e8f0',
      activeNavBg: '#f1f5f9',
      activeNavText: '#0f172a',
      inactiveNavText: '#334155',
      navHoverBg: '#f8fafc',
      sectionHeaderText: '#64748b',
      mainBg: '#f8fafc',
      cardBg: '#ffffff',
      cardBorder: '#e2e8f0',
      accent: '#16a34a',
      accentLight: '#dcfce7',
      danger: '#dc2626',
      dangerLight: '#fef2f2',
      warning: '#d97706',
      info: '#2563eb',
      infoLight: '#eff6ff',
      textPrimary: '#0f172a',
      textSecondary: '#334155',
      textMuted: '#64748b',
      headerBg: '#ffffff',
      tableHeader: '#f8fafc',
      tableRowHover: '#f1f5f9',
      tableBorder: '#e2e8f0',
      badgeGreen: { bg: '#dcfce7', text: '#166534' },
      badgeRed: { bg: '#fee2e2', text: '#991b1b' },
      badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
      badgeGray: { bg: '#f1f5f9', text: '#334155' },
      modalOverlay: 'rgba(0, 0, 0, 0.4)',
      modalBg: '#ffffff',
      pureWhite: '#ffffff',
      iconBright: '#f59e0b',
      collapseButtonBg: '#f1f5f9',
      collapseButtonHover: '#e2e8f0',
      collapseButtonText: '#0f172a',
      tooltipBg: '#1e293b',
      tooltipText: '#ffffff',
      filterSection: '#f8fafc',
    }
  };

  const theme = colors[effectiveTheme];

  // ─── DESKTOP STYLES ───────────────────────────────────────────────────────────
  const desktopStyles = {
    card: {
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    },
    tableWrapper: {
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      marginTop: '16px',
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '11px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.textSecondary,
      background: theme.tableHeader,
      borderBottom: `1px solid ${theme.tableBorder}`,
      borderRight: `1px solid ${theme.tableBorder}`,
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '13px 16px',
      fontSize: '13.5px',
      color: theme.textPrimary,
      borderBottom: `1px solid ${theme.tableBorder}`,
      borderRight: `1px solid ${theme.tableBorder}`,
      verticalAlign: 'middle',
    },
    badge: (type) => {
      const map = {
        green: theme.badgeGreen,
        red: theme.badgeRed,
        blue: theme.badgeBlue,
        gray: theme.badgeGray,
      };
      const c = map[type] || map.gray;
      return {
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '600',
        background: c.bg, color: c.text,
      };
    },
    actionButton: {
      padding: '6px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      width: '60px',
      height: '28px',
      transition: 'all 0.15s ease',
    },
    input: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: `1px solid ${theme.cardBorder}`,
      fontSize: '13px',
      color: theme.textPrimary,
      background: theme.cardBg,
      width: '100%',
      outline: 'none',
    },
    stickyHeader: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: theme.headerBg,
      padding: '16px 28px',
      borderBottom: `1px solid ${theme.cardBorder}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '20px',
    },
    navItem: (active, collapsed) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: collapsed ? '0' : '12px',
      padding: collapsed ? '12px 0' : '10px 14px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '13.5px',
      fontWeight: active ? '600' : '500',
      color: active ? theme.activeNavText : theme.inactiveNavText,
      background: active ? theme.activeNavBg : 'transparent',
      transition: 'all 0.15s ease',
      border: 'none',
      width: '100%',
      textAlign: collapsed ? 'center' : 'left',
      marginBottom: '2px',
      position: 'relative',
    }),
    modalOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: theme.modalOverlay,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)',
    },
    modalContent: {
      background: theme.modalBg,
      borderRadius: '16px',
      padding: '32px',
      maxWidth: '500px',
      width: '90%',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
  };

  // ─── MOBILE STYLES ────────────────────────────────────────────────────────────
  const mobileStyles = {
    // Root container
    root: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "'Inter', -apple-system, sans-serif",
      WebkitFontSmoothing: 'antialiased',
      overflowX: 'hidden',
    },
    // Top navigation bar
    topNav: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      background: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      height: '56px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    },
    topNavTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#0f172a',
    },
    topNavSub: {
      fontSize: '11px',
      color: '#64748b',
      marginTop: '1px',
    },
    menuBtn: {
      background: 'none',
      border: 'none',
      fontSize: '22px',
      cursor: 'pointer',
      padding: '6px',
      color: '#0f172a',
      display: 'flex',
      alignItems: 'center',
    },
    // Stats bar below top nav
    statsBar: {
      position: 'fixed',
      top: '56px',
      left: 0,
      right: 0,
      zIndex: 90,
      background: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      padding: '8px 16px',
      gap: '10px',
    },
    statPill: {
      flexShrink: 0,
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '20px',
      padding: '6px 14px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    statPillLabel: {
      fontSize: '10px',
      color: '#64748b',
      fontWeight: '500',
    },
    statPillValue: {
      fontSize: '13px',
      fontWeight: '700',
      color: '#0f172a',
      whiteSpace: 'nowrap',
    },
    // Tab bar at bottom
    bottomTabBar: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      background: '#ffffff',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      height: '60px',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
    },
    tabItem: (active) => ({
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '3px',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: '6px 0',
      borderTop: active ? '2px solid #2563eb' : '2px solid transparent',
    }),
    tabIcon: (active) => ({
      fontSize: '20px',
      filter: active ? 'none' : 'grayscale(0.5) opacity(0.6)',
    }),
    tabLabel: (active) => ({
      fontSize: '10px',
      fontWeight: active ? '700' : '500',
      color: active ? '#2563eb' : '#64748b',
    }),
    // Main scrollable content
    mainContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: '116px', // topNav + statsBar
      paddingBottom: '72px', // bottomTabBar
      padding: '116px 0 72px',
    },
    // Section container
    section: {
      padding: '16px',
    },
    // Card
    card: {
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '12px',
    },
    cardHeader: {
      padding: '12px 16px',
      borderBottom: '1px solid #f1f5f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    cardTitle: {
      fontSize: '13px',
      fontWeight: '700',
      color: '#0f172a',
    },
    // Sale/Expense list item
    listItem: {
      padding: '14px 16px',
      borderBottom: '1px solid #f1f5f9',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '12px',
    },
    listItemLeft: {
      flex: 1,
      minWidth: 0,
    },
    listItemAmount: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#0f172a',
      marginBottom: '3px',
    },
    listItemReason: {
      fontSize: '13px',
      color: '#475569',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    listItemMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginTop: '4px',
    },
    badge: (color) => {
      const map = {
        green: { bg: '#dcfce7', text: '#166534' },
        blue: { bg: '#dbeafe', text: '#1e40af' },
        gray: { bg: '#f1f5f9', text: '#334155' },
        red: { bg: '#fee2e2', text: '#991b1b' },
        amber: { bg: '#fef3c7', text: '#92400e' },
      };
      const c = map[color] || map.gray;
      return {
        fontSize: '10px',
        fontWeight: '600',
        padding: '2px 8px',
        borderRadius: '10px',
        background: c.bg,
        color: c.text,
        whiteSpace: 'nowrap',
      };
    },
    deleteBtn: {
      background: '#fee2e2',
      border: 'none',
      borderRadius: '8px',
      color: '#dc2626',
      fontSize: '12px',
      fontWeight: '600',
      padding: '6px 10px',
      cursor: 'pointer',
      flexShrink: 0,
      height: '32px',
      display: 'flex',
      alignItems: 'center',
    },
    disabledDeleteBtn: {
      background: '#f1f5f9',
      border: 'none',
      borderRadius: '8px',
      color: '#94a3b8',
      fontSize: '12px',
      fontWeight: '600',
      padding: '6px 10px',
      cursor: 'not-allowed',
      flexShrink: 0,
      height: '32px',
      display: 'flex',
      alignItems: 'center',
    },
    // FAB (floating action button)
    fab: {
      position: 'fixed',
      bottom: '72px',
      right: '16px',
      zIndex: 90,
      width: '52px',
      height: '52px',
      borderRadius: '50%',
      background: '#2563eb',
      color: '#ffffff',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 16px rgba(37,99,235,0.4)',
    },
    // Bottom sheet modal
    bottomSheetOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)',
      zIndex: 200,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    bottomSheet: {
      background: '#ffffff',
      borderRadius: '20px 20px 0 0',
      padding: '20px 20px 40px',
      maxHeight: '85vh',
      overflowY: 'auto',
    },
    bottomSheetHandle: {
      width: '40px',
      height: '4px',
      background: '#e2e8f0',
      borderRadius: '2px',
      margin: '0 auto 20px',
    },
    bottomSheetTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#0f172a',
      marginBottom: '20px',
    },
    // Form in bottom sheet
    formGroup: {
      marginBottom: '16px',
    },
    formLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '6px',
      display: 'block',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    formInput: {
      width: '100%',
      padding: '12px 14px',
      borderRadius: '10px',
      border: '1.5px solid #e2e8f0',
      fontSize: '15px',
      color: '#0f172a',
      background: '#ffffff',
      outline: 'none',
      boxSizing: 'border-box',
      WebkitAppearance: 'none',
    },
    formSelect: {
      width: '100%',
      padding: '12px 14px',
      borderRadius: '10px',
      border: '1.5px solid #e2e8f0',
      fontSize: '15px',
      color: '#0f172a',
      background: '#ffffff',
      outline: 'none',
      boxSizing: 'border-box',
      WebkitAppearance: 'none',
    },
    primaryBtn: {
      width: '100%',
      padding: '14px',
      borderRadius: '12px',
      border: 'none',
      background: '#2563eb',
      color: '#ffffff',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '8px',
    },
    dangerBtn: {
      width: '100%',
      padding: '14px',
      borderRadius: '12px',
      border: 'none',
      background: '#dc2626',
      color: '#ffffff',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '8px',
    },
    secondaryBtn: {
      width: '100%',
      padding: '14px',
      borderRadius: '12px',
      border: '1.5px solid #e2e8f0',
      background: '#f8fafc',
      color: '#0f172a',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '8px',
    },
    // Slide-out menu
    menuOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)',
      zIndex: 200,
    },
    menuPanel: {
      position: 'fixed',
      top: 0, left: 0, bottom: 0,
      width: '280px',
      background: '#ffffff',
      zIndex: 201,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '4px 0 16px rgba(0,0,0,0.12)',
    },
    menuHeader: {
      padding: '20px 20px 16px',
      borderBottom: '1px solid #f1f5f9',
      background: '#f8fafc',
    },
    menuItem: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '14px 20px',
      cursor: 'pointer',
      background: active ? '#eff6ff' : 'none',
      border: 'none',
      width: '100%',
      textAlign: 'left',
      borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
    }),
    menuItemIcon: {
      fontSize: '20px',
      width: '24px',
      textAlign: 'center',
    },
    menuItemLabel: (active) => ({
      fontSize: '14px',
      fontWeight: active ? '700' : '500',
      color: active ? '#2563eb' : '#0f172a',
    }),
    // Summary section
    summaryCard: {
      background: '#2563eb',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '12px',
      color: '#ffffff',
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '10px',
      marginTop: '16px',
    },
    summaryItem: {
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '10px',
      padding: '12px',
    },
    summaryItemLabel: {
      fontSize: '11px',
      opacity: 0.85,
      marginBottom: '4px',
    },
    summaryItemValue: {
      fontSize: '16px',
      fontWeight: '700',
    },
    // Empty state
    emptyState: {
      padding: '40px 20px',
      textAlign: 'center',
      color: '#64748b',
    },
    emptyIcon: {
      fontSize: '40px',
      marginBottom: '12px',
    },
    emptyText: {
      fontSize: '14px',
      color: '#64748b',
    },
    // Filter chips row
    filterChipsRow: {
      display: 'flex',
      overflowX: 'auto',
      padding: '10px 16px',
      gap: '8px',
      scrollbarWidth: 'none',
      background: '#ffffff',
      borderBottom: '1px solid #f1f5f9',
    },
    filterChip: (active) => ({
      flexShrink: 0,
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      border: active ? '1.5px solid #2563eb' : '1.5px solid #e2e8f0',
      background: active ? '#eff6ff' : '#ffffff',
      color: active ? '#2563eb' : '#64748b',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }),
  };

  // ─── TIME CHECK ───────────────────────────────────────────────────────────────
  function isTimeInRange(startHour, startMinute, endHour, endMinute) {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, endMinute);
    return now >= start && now <= end;
  }

  // ─── INITIALIZATION EFFECTS ──────────────────────────────────────────────────
  useEffect(() => {
    const updateTime = () => setCurrentTime(new Date());
    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isTimeInRange(23, 0, 23, 1) || isTimeInRange(11, 0, 11, 1)) {
      document.body.innerHTML = `
        <div style="text-align:center;font-family:Inter,sans-serif;padding:40px;background:#fff;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#0f172a;">
          <div style="width:40px;height:40px;border-radius:50%;border:3px solid #e2e8f0;border-top:3px solid #2563eb;animation:spin 0.8s linear infinite;margin-bottom:20px;"></div>
          <div style="font-size:15px;">Checking shift validity...</div>
          <style>@keyframes spin{to{transform:rotate(360deg);}}</style>
        </div>`;
      setTimeout(() => window.location.reload(), 60000);
    }
  }, []);

  useEffect(() => {
    const checkTheme = async () => {
      try {
        const res = await fetch(urls.security, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenFromUrl }),
        });
        if (res.ok) {
          const data = await res.json();
          const themeColor = data.colour || '';
          if (!themeColor || themeColor.toLowerCase() === 'white' || themeColor.toLowerCase() === 'null') {
            setCurrentTheme('white');
          } else {
            setCurrentTheme('blue');
          }
        }
      } catch (e) { console.error('Theme check error', e); }
    };
    if (tokenFromUrl) checkTheme();
  }, [tokenFromUrl]);

  useEffect(() => {
    const initializePage = async () => {
      if (!tokenFromUrl) { navigate('/login'); return; }
      try {
        const secRes = await fetch(urls.security, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenFromUrl }),
        });
        if (secRes.ok) {
          const secData = await secRes.json();
          if (secData.message === 'Session valid') {
            setEmployeeName(secData.employee_name);
            setClinicName(secData.clinic);
            setEmployee(secData.employee_name);

            const shiftRes = await fetch(urls.checkshift, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: tokenFromUrl }),
            });
            if (shiftRes.ok) {
              const shiftData = await shiftRes.json();
              if (shiftData.shiftType) {
                setSelectedShift(shiftData.shiftType);
                setShiftDate(shiftData.shiftDate);
              }
            }

            const permRes = await fetch(urls.fetchpermissions, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: tokenFromUrl }),
            });
            if (permRes.ok) {
              const data = await permRes.json();
              setHasSalesHistoryAccess((data.permissions || []).includes('access-sales-details'));
            }

            const catRes = await fetch(urls.fetchcategories, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: tokenFromUrl }),
            });
            if (catRes.ok) {
              const data = await catRes.json();
              if (data.success) {
                setFetchedSalesCategories(data.categories.filter(c => c.type === 'sales').map(c => c.category_name));
                setExpenseCategories([...data.categories.filter(c => c.type === 'expense').map(c => c.category_name), 'non-categorized']);
              }
            }
          } else if (secData.error === 'Session expired') {
            navigate(`/dashboard?token=${secData.clinic_session_token}`);
          } else {
            navigate('/login');
          }
        }
      } catch (e) {
        console.error('Init error', e);
        navigate('/login');
      } finally {
        setIsValidating(false);
        setIsLoading(false);
      }
    };
    initializePage();
  }, [tokenFromUrl, navigate]);

  useEffect(() => {
    if (selectedShift && shiftDate) {
      fetchSalesData();
      fetchExpensesData();
      const id = setInterval(() => { fetchSalesData(); fetchExpensesData(); }, 15000);
      return () => clearInterval(id);
    }
  }, [selectedShift, shiftDate]);

  useEffect(() => {
    const fs = getFilteredSales();
    const st = fs.reduce((t, s) => t + Math.round(parseFloat(s.amount)), 0);
    setTotalSales(st);
    const fe = getFilteredExpenses();
    const et = fe.reduce((t, e) => t + Math.round(parseFloat(e.amount)), 0);
    setTotalExpenses(et);
    setCashAtHand(st - et);
  }, [salesData, expensesData, selectedServedByFilters, selectedCategoryFilters, filterType, selectedExpenseTakenByFilters, selectedExpenseCategoryFilters, expenseFilterType]);

  useEffect(() => {
    setAvailableServedBy([...new Set(salesData.map(s => s.servedBy))].filter(Boolean));
    setAvailableCategories([...new Set(salesData.map(s => s.category || 'non-categorized'))].filter(Boolean));
  }, [salesData]);

  useEffect(() => {
    setAvailableExpenseTakenBy([...new Set(expensesData.map(e => e.takenBy))].filter(Boolean));
    setAvailableExpenseCategories([...new Set(expensesData.map(e => e.category || 'non-categorized'))].filter(Boolean));
  }, [expensesData]);

  useEffect(() => {
    if (shiftDate) {
      const date = new Date(shiftDate);
      setFormattedDate(new Intl.DateTimeFormat('en-GB', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        timeZone: 'Africa/Kampala',
      }).format(date));
    }
  }, [shiftDate]);

  useEffect(() => {
    if (isSendingWhatsApp) {
      const interval = setInterval(() => {
        setMessageIndex(prev => {
          if (prev < messages.length - 1) return prev + 1;
          clearInterval(interval);
          return prev;
        });
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isSendingWhatsApp]);

  // ─── DATA FETCHING ────────────────────────────────────────────────────────────
  const fetchSalesData = async () => {
    try {
      const res = await fetch(`${API_URL}/fetchsales.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: shiftDate, shift: selectedShift, token: tokenFromUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        setSalesData(data.map(s => ({
          amount: Math.round(parseFloat(s.Amount)),
          reason: s.Reason,
          servedBy: s.ServedBy,
          category: s.Category || 'non-categorized',
          id: s.SaleID
        })));
      }
    } catch (e) { toast.error('Failed to fetch sales data'); }
  };

  const fetchExpensesData = async () => {
    try {
      const res = await fetch(`${API_URL}/fetchexpenses.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: shiftDate, shift: selectedShift, token: tokenFromUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        setExpensesData(data.map(e => ({
          amount: Math.round(parseFloat(e.Amount)),
          details: e.Details,
          takenBy: e.TakenBy,
          servedBy: e.ServedBy,
          category: e.category || 'non-categorized',
          id: e.ExpenseID
        })));
      }
    } catch (e) { toast.error('Failed to fetch expenses data'); }
  };

  // ─── FILTER FUNCTIONS ─────────────────────────────────────────────────────────
  const getFilteredSales = () => {
    let f = salesData;
    if (filterType === 'servedBy' && selectedServedByFilters.length > 0)
      f = f.filter(s => selectedServedByFilters.includes(s.servedBy));
    if (filterType === 'category' && selectedCategoryFilters.length > 0)
      f = f.filter(s => selectedCategoryFilters.includes(s.category || 'non-categorized'));
    return f;
  };

  const getFilteredExpenses = () => {
    let f = expensesData;
    if (expenseFilterType === 'takenBy' && selectedExpenseTakenByFilters.length > 0)
      f = f.filter(e => selectedExpenseTakenByFilters.includes(e.takenBy));
    if (expenseFilterType === 'category' && selectedExpenseCategoryFilters.length > 0)
      f = f.filter(e => selectedExpenseCategoryFilters.includes(e.category || 'non-categorized'));
    return f;
  };

  // ─── ACTION HANDLERS ──────────────────────────────────────────────────────────
  const handleAddSale = async () => {
    setIsAddingSale(true);
    try {
      if (!/^-?\d+(\.\d+)?$/.test(newSale.amount)) {
        toast.error('Invalid amount'); setIsAddingSale(false); return;
      }
      const requestData = {
        amount: Math.round(parseFloat(newSale.amount)),
        reason: newSale.reason,
        category: newSale.category,
        servedBy: employee,
        shift: selectedShift,
        date: currentTime.toISOString().split('T')[0],
        token: tokenFromUrl,
      };
      if (!requestData.amount || !requestData.reason || !requestData.category) {
        toast.error('All fields must be filled out'); setIsAddingSale(false); return;
      }
      const res = await fetch(urls.sales, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData),
      });
      if (res.ok) {
        setNewSale({ amount: 0, reason: '', category: '', servedBy: employee });
        await fetchSalesData();
        toast.success('Sale added successfully!');
        setShowMobileAddSale(false);
      } else { toast.error('Failed to add sale'); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setIsAddingSale(false); }
  };

  const handleAddExpense = async () => {
    setIsAddingExpense(true);
    try {
      if (!/^-?\d+(\.\d+)?$/.test(newExpense.amount)) {
        toast.error('Invalid amount'); setIsAddingExpense(false); return;
      }
      const requestData = {
        amount: Math.round(parseFloat(newExpense.amount)),
        details: newExpense.details,
        takenBy: newExpense.takenBy,
        servedBy: employee,
        category: newExpense.category,
        shift: selectedShift,
        date: currentTime.toISOString().split('T')[0],
        token: tokenFromUrl
      };
      const missing = Object.entries(requestData).filter(([k,v]) => !v && k !== 'date' && k !== 'token').map(([k]) => k);
      if (missing.length > 0) { toast.error(`Missing: ${missing.join(', ')}`); setIsAddingExpense(false); return; }
      const res = await fetch(urls.expenses, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData),
      });
      if (res.ok) {
        setNewExpense({ amount: 0, details: '', takenBy: '', servedBy: employee, category: '' });
        await fetchExpensesData();
        toast.success('Expense added successfully!');
        setShowMobileAddExpense(false);
      } else { toast.error('Failed to add expense'); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setIsAddingExpense(false); }
  };

  const handleDeleteSale = async (id) => {
    setDeletingSaleId(id);
    const s = salesData.find(x => x.id === id);
    if (!s) { setDeletingSaleId(null); return; }
    try {
      const res = await fetch(urls.deletesale, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: s.amount, reason: s.reason, category: s.category, servedBy: s.servedBy, shift: selectedShift, date: new Date().toISOString().split('T')[0], token: tokenFromUrl }),
      });
      if (res.ok) { setSalesData(prev => prev.filter(x => x.id !== id)); toast.success('Sale deleted'); }
      else {
        const err = await res.json();
        if (err.error?.includes('not authorized')) {
          const contact = err.error.split('Please contact:')[1]?.trim().split(' or ')[0]?.trim();
          toast.error(`Not authorized. Contact ${contact || 'administrator'}.`, { autoClose: 60000 });
        } else { toast.error(err.message || 'Failed to delete sale'); }
      }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setDeletingSaleId(null); }
  };

  const handleDeleteExpense = async (id) => {
    setDeletingExpenseId(id);
    const e = expensesData.find(x => x.id === id);
    if (!e) { setDeletingExpenseId(null); return; }
    try {
      const res = await fetch(urls.deleteexpense, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: e.amount, details: e.details, takenBy: e.takenBy, servedBy: e.servedBy, category: e.category, shift: selectedShift, date: new Date().toISOString().split('T')[0], token: tokenFromUrl }),
      });
      if (res.ok) { setExpensesData(prev => prev.filter(x => x.id !== id)); toast.success('Expense deleted'); }
      else { toast.error('Failed to delete expense'); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setDeletingExpenseId(null); }
  };

  const handleSalesHistoryClick = () => {
    if (hasSalesHistoryAccess) navigate(`/access-sales-details/?token=${tokenFromUrl}`);
    else toast.error('Unauthorized. Please talk to the administrator.');
  };

  const handleCloseShiftConfirm = () => { setShowCloseShiftPrompt(false); setShowCommentsModal(true); };

  const handleCommentsSubmit = async () => {
    if (!comments.trim()) { toast.error('Please enter comments before closing.'); return; }
    setShowCommentsModal(false);
    await performCloseShift();
  };

  const performCloseShift = async () => {
    setIsClosingShift(true);
    setAnimationProgress(0); setCompletedSteps([]); setShowSuccessAnimation(false);
    try {
      const salesByCategory = {};
      getFilteredSales().forEach(s => {
        const c = s.category || 'non-categorized';
        salesByCategory[c] = (salesByCategory[c] || 0) + Math.round(parseFloat(s.amount));
      });
      const expensesByCategory = {};
      getFilteredExpenses().forEach(e => {
        const c = e.category || 'non-categorized';
        expensesByCategory[c] = (expensesByCategory[c] || 0) + Math.round(parseFloat(e.amount));
      });
      const requestData = { comments, shift: selectedShift, shiftDate, employee, token: tokenFromUrl, totalSales, totalExpenses, salesByCategory, expensesByCategory, cashAtHand };

      const progressInterval = setInterval(() => {
        setAnimationProgress(prev => { if (prev >= 100) { clearInterval(progressInterval); return 100; } return prev + 1; });
      }, 150);

      const closeRes = await fetch(urls.closeshift, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData) });
      if (!closeRes.ok) throw new Error('Shift close failed');

      setCompletedSteps(prev => [...prev, 'shift_closed']);
      setIsSendingWhatsApp(true);
      setMessageIndex(0);


      
      const adminRes = await fetch(urls.whatsappAdmin, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData) });
      if (adminRes.ok) {
        setCompletedSteps(prev => [...prev, 'admin_notified']);
        const adminData = await adminRes.json();
        const beemRes = await fetch(urls.whatsappBeem, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...requestData, adminResponse: adminData }) });
        if (beemRes.ok) {
          const beemData = await beemRes.json();
          if (beemData.success === true) { setCompletedSteps(prev => [...prev, 'beem_notified']); toast.success('Shift report sent to administrators.'); }
          else { toast.error('Admin has not received the SMS. Please contact support.'); }
        }
      } else { toast.error('WhatsApp message not sent to Admin.'); }

      clearInterval(progressInterval);
      setAnimationProgress(100);
      setShowSuccessAnimation(true);
      setTimeout(() => { setIsClosingShift(false); setIsSendingWhatsApp(false); navigate('/'); }, 3000);
    } catch (e) {
      console.error('Close shift error', e);
      toast.error('Failed to close shift');
      setIsClosingShift(false);
    }
  };

  const formatCurrency = (v) => {
    if (v === null || v === undefined) return 'UGX 0';
    const n = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(n)) return 'UGX 0';
    return `UGX ${Math.round(n).toLocaleString('en-UG')}`;
  };

  const formatNumber = (v) => {
    if (v === null || v === undefined) return '0';
    const n = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(n)) return '0';
    return Math.round(n).toLocaleString('en-UG');
  };

  const allSalesCategories = [...new Set([...hardcodedSalesCategories, ...fetchedSalesCategories])];

  // ─── LOADING STATE ────────────────────────────────────────────────────────────
  if (isValidating || isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: isMobile ? '#fff' : theme.sidebarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '50%', border: '3px solid #e2e8f0', borderTop: '3px solid #2563eb', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ color: isMobile ? '#64748b' : theme.inactiveNavText, fontSize: '14px' }}>Loading Sales Dashboard...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!selectedShift) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', textAlign: 'center', border: '1px solid #e2e8f0', maxWidth: '340px', width: '100%' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏰</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>No Active Shift</div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>There is no active shift for this time period.</div>
        </div>
      </div>
    );
  }

  // ─── CLOSE SHIFT ANIMATION (shared) ──────────────────────────────────────────
  const renderCloseShiftAnimation = () => isSendingWhatsApp && (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.97)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '32px' }}>
      <div style={{ width: '90%', maxWidth: '400px', height: '6px', background: '#e2e8f0', borderRadius: '3px', marginBottom: '32px', overflow: 'hidden' }}>
        <div style={{ width: `${animationProgress}%`, height: '100%', background: '#2563eb', borderRadius: '3px', transition: 'width 0.3s ease' }} />
      </div>
      <div style={{ width: '80px', height: '80px', background: '#dbeafe', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', position: 'relative' }}>
        <span style={{ fontSize: '32px', fontWeight: '700', color: '#2563eb' }}>SMS</span>
        {showSuccessAnimation && (
          <div style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#16a34a', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px' }}>✓</div>
        )}
      </div>
      <div style={{ fontSize: '16px', fontWeight: '600', color: '#2563eb', textAlign: 'center', marginBottom: '24px' }}>{messages[messageIndex]}</div>
      <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
        {[['shift_closed', 'Shift Closed'], ['admin_notified', 'Admin SMS'], ['beem_notified', 'SMS Sent']].map(([key, label]) => (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: completedSteps.includes(key) ? '#2563eb' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#fff' }}>
              {completedSteps.includes(key) ? '✓' : ''}
            </div>
            <span style={{ fontSize: '11px', color: '#64748b' }}>{label}</span>
          </div>
        ))}
      </div>
      {showSuccessAnimation && (
        <div style={{ padding: '12px 24px', background: '#2563eb', color: '#fff', borderRadius: '20px', fontWeight: '700', fontSize: '14px' }}>Shift Closed Successfully!</div>
      )}
    </div>
  );

  // ─── MODAL (shared for both desktop and mobile) ───────────────────────────────
  const renderConfirmationModals = () => (
    <>
      {showCloseShiftPrompt && (
        <div style={isMobile ? mobileStyles.bottomSheetOverlay : desktopStyles.modalOverlay} onClick={isMobile ? () => setShowCloseShiftPrompt(false) : undefined}>
          <div style={isMobile ? mobileStyles.bottomSheet : desktopStyles.modalContent} onClick={e => e.stopPropagation()}>
            {isMobile && <div style={mobileStyles.bottomSheetHandle} />}
            <div style={isMobile ? { fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' } : desktopStyles.modalContent}>
        
        
              {!isMobile && <div style={{ fontSize: '20px', fontWeight: '700', color: theme.textPrimary, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}><span>⚠️</span> Close Shift Confirmation</div>}
              {isMobile && <div style={mobileStyles.bottomSheetTitle}>⚠️ Close Shift</div>}
            </div>
            <div style={{ fontSize: '14px', color: isMobile ? '#475569' : theme.textSecondary, marginBottom: '20px', lineHeight: '1.6' }}>
              <strong>This action is irreversible!</strong><br />
              Make sure you have collected all money for your shift. Are you sure you want to proceed?
            </div>
            <button style={isMobile ? mobileStyles.dangerBtn : { ...desktopStyles.actionButton, background: theme.danger, color: '#fff', width: '100%', height: '44px', borderRadius: '10px', fontSize: '14px' }} onClick={handleCloseShiftConfirm}>Yes, Proceed</button>
            <button style={isMobile ? { ...mobileStyles.secondaryBtn, marginTop: '8px' } : { ...desktopStyles.actionButton, background: theme.tableHeader, color: theme.textPrimary, width: '100%', height: '44px', borderRadius: '10px', fontSize: '14px', marginTop: '8px' }} onClick={() => setShowCloseShiftPrompt(false)}>Cancel</button>
          </div>
        </div>
      )}
      {showCommentsModal && (
        <div style={isMobile ? mobileStyles.bottomSheetOverlay : desktopStyles.modalOverlay} onClick={isMobile ? () => setShowCommentsModal(false) : undefined}>
          <div style={isMobile ? mobileStyles.bottomSheet : desktopStyles.modalContent} onClick={e => e.stopPropagation()}>
            {isMobile && <div style={mobileStyles.bottomSheetHandle} />}
            <div style={isMobile ? mobileStyles.bottomSheetTitle : { fontSize: '20px', fontWeight: '700', color: theme.textPrimary, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isMobile ? '📝 Shift Comments' : <><span>📝</span> Shift Comments</>}
            </div>
            <p style={{ fontSize: '14px', color: isMobile ? '#475569' : theme.textSecondary, marginBottom: '16px' }}>Please enter your shift comments before closing:</p>
            <textarea
              style={isMobile ? { ...mobileStyles.formInput, minHeight: '100px', resize: 'vertical' } : { width: '100%', padding: '12px', borderRadius: '8px', border: `1px solid ${theme.cardBorder}`, fontSize: '14px', color: theme.textPrimary, background: theme.cardBg, marginBottom: '20px', resize: 'vertical', minHeight: '100px', outline: 'none' }}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Enter shift comments here..."
              autoFocus
            />
            <button style={isMobile ? { ...mobileStyles.primaryBtn, marginTop: '12px', opacity: !comments.trim() ? 0.5 : 1 } : { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: 'pointer', background: theme.info, color: '#fff', marginTop: '8px', width: '100%' }} onClick={handleCommentsSubmit} disabled={!comments.trim()}>Close Shift</button>
            <button style={isMobile ? mobileStyles.secondaryBtn : { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: 'pointer', background: theme.tableHeader, color: theme.textPrimary, marginTop: '8px', width: '100%' }} onClick={() => { setShowCommentsModal(false); setComments(''); }}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );

  // ══════════════════════════════════════════════════════════════════════════════
  // ─── MOBILE LAYOUT ────────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    const filteredSales = getFilteredSales();
    const filteredExpenses = getFilteredExpenses();

    const salesByCategory = {};
    filteredSales.forEach(s => { const c = s.category || 'non-categorized'; salesByCategory[c] = (salesByCategory[c] || 0) + s.amount; });
    const expensesByCategory = {};
    filteredExpenses.forEach(e => { const c = e.category || 'non-categorized'; expensesByCategory[c] = (expensesByCategory[c] || 0) + e.amount; });

    return (
      <div style={mobileStyles.root}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
          body { overflow-x: hidden; }
          input, select, textarea { font-family: 'Inter', sans-serif; }
          ::-webkit-scrollbar { display: none; }
        `}</style>

        {/* ── Fixed Top Navigation ─────────────────────────────────────────── */}
        <div style={mobileStyles.topNav}>
          <button style={mobileStyles.menuBtn} onClick={() => setMobileMenuOpen(true)}>☰</button>
          <div style={{ textAlign: 'center' }}>
            <div style={mobileStyles.topNavTitle}>{clinicName || 'SmartShop'}</div>
            <div style={mobileStyles.topNavSub}>{selectedShift} Shift · {employeeName}</div>
          </div>
          <button
            style={{ ...mobileStyles.menuBtn, background: '#fee2e2', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', fontWeight: '700', color: '#dc2626' }}
            onClick={() => setShowCloseShiftPrompt(true)}
          >Close</button>
        </div>

        {/* ── Fixed Stats Bar ───────────────────────────────────────────────── */}
        <div style={mobileStyles.statsBar}>
          <div style={mobileStyles.statPill}>
            <span style={mobileStyles.statPillLabel}>Sales</span>
            <span style={{ ...mobileStyles.statPillValue, color: '#2563eb' }}>{formatNumber(totalSales)}</span>
          </div>
          <div style={mobileStyles.statPill}>
            <span style={mobileStyles.statPillLabel}>Expenses</span>
            <span style={{ ...mobileStyles.statPillValue, color: '#dc2626' }}>{formatNumber(totalExpenses)}</span>
          </div>
          <div style={mobileStyles.statPill}>
            <span style={mobileStyles.statPillLabel}>Cash at Hand</span>
            <span style={{ ...mobileStyles.statPillValue, color: cashAtHand >= 0 ? '#16a34a' : '#dc2626' }}>{formatNumber(cashAtHand)}</span>
          </div>
          <div style={mobileStyles.statPill}>
            <span style={mobileStyles.statPillLabel}>Entries</span>
            <span style={mobileStyles.statPillValue}>{filteredSales.length + filteredExpenses.length}</span>
          </div>
        </div>

        {/* ── Main Scrollable Content ───────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', paddingTop: '116px', paddingBottom: '72px' }}>

          {/* ── SALES VIEW ─────────────────────────────────────────────────── */}
          {activeView === 'sales' && (
            <>
              {/* Filter chips */}
              {availableServedBy.length > 0 && (
                <div style={mobileStyles.filterChipsRow}>
                  <button style={mobileStyles.filterChip(selectedServedByFilters.length === 0)} onClick={() => setSelectedServedByFilters([])}>All</button>
                  {availableServedBy.map(name => (
                    <button key={name} style={mobileStyles.filterChip(selectedServedByFilters.includes(name))}
                      onClick={() => setSelectedServedByFilters(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name])}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
              <div style={mobileStyles.section}>
                <div style={mobileStyles.card}>
                  <div style={mobileStyles.cardHeader}>
                    <span style={mobileStyles.cardTitle}>💰 Sales ({filteredSales.length})</span>
                    <span style={{ ...mobileStyles.badge('blue') }}>{formatCurrency(totalSales)}</span>
                  </div>
                  {filteredSales.length === 0 ? (
                    <div style={mobileStyles.emptyState}>
                      <div style={mobileStyles.emptyIcon}>💰</div>
                      <div style={mobileStyles.emptyText}>No sales recorded yet</div>
                    </div>
                  ) : (
                    filteredSales.map((sale, index) => (
                      <div key={sale.id || index} style={mobileStyles.listItem}>
                        <div style={mobileStyles.listItemLeft}>
                          <div style={mobileStyles.listItemAmount}>{formatCurrency(sale.amount)}</div>
                          <div style={mobileStyles.listItemReason}>{sale.reason}</div>
                          <div style={mobileStyles.listItemMeta}>
                            <span style={mobileStyles.badge('gray')}>{sale.category}</span>
                            <span style={mobileStyles.badge('blue')}>{sale.servedBy}</span>
                          </div>
                        </div>
                        {index !== 0 ? (
                          <button style={{ ...mobileStyles.deleteBtn, opacity: deletingSaleId === sale.id ? 0.5 : 1 }}
                            onClick={() => handleDeleteSale(sale.id)} disabled={deletingSaleId === sale.id}>
                            {deletingSaleId === sale.id ? '...' : 'Del'}
                          </button>
                        ) : (
                          <button style={mobileStyles.disabledDeleteBtn} disabled>Del</button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── EXPENSES VIEW ───────────────────────────────────────────────── */}
          {activeView === 'expenses' && (
            <>
              {availableExpenseTakenBy.length > 0 && (
                <div style={mobileStyles.filterChipsRow}>
                  <button style={mobileStyles.filterChip(selectedExpenseTakenByFilters.length === 0)} onClick={() => setSelectedExpenseTakenByFilters([])}>All</button>
                  {availableExpenseTakenBy.map(name => (
                    <button key={name} style={mobileStyles.filterChip(selectedExpenseTakenByFilters.includes(name))}
                      onClick={() => setSelectedExpenseTakenByFilters(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name])}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
              <div style={mobileStyles.section}>
                <div style={mobileStyles.card}>
                  <div style={mobileStyles.cardHeader}>
                    <span style={mobileStyles.cardTitle}>📤 Expenses ({filteredExpenses.length})</span>
                    <span style={{ ...mobileStyles.badge('red') }}>{formatCurrency(totalExpenses)}</span>
                  </div>
                  {filteredExpenses.length === 0 ? (
                    <div style={mobileStyles.emptyState}>
                      <div style={mobileStyles.emptyIcon}>📤</div>
                      <div style={mobileStyles.emptyText}>No expenses recorded yet</div>
                    </div>
                  ) : (
                    filteredExpenses.map((expense, index) => (
                      <div key={expense.id || index} style={mobileStyles.listItem}>
                        <div style={mobileStyles.listItemLeft}>
                          <div style={mobileStyles.listItemAmount}>{formatCurrency(expense.amount)}</div>
                          <div style={mobileStyles.listItemReason}>{expense.details}</div>
                          <div style={mobileStyles.listItemMeta}>
                            <span style={mobileStyles.badge('gray')}>{expense.category}</span>
                            <span style={mobileStyles.badge('amber')}>{expense.takenBy}</span>
                          </div>
                        </div>
                        {index !== 0 ? (
                          <button style={{ ...mobileStyles.deleteBtn, opacity: deletingExpenseId === expense.id ? 0.5 : 1 }}
                            onClick={() => handleDeleteExpense(expense.id)} disabled={deletingExpenseId === expense.id}>
                            {deletingExpenseId === expense.id ? '...' : 'Del'}
                          </button>
                        ) : (
                          <button style={mobileStyles.disabledDeleteBtn} disabled>Del</button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── SUMMARY VIEW ────────────────────────────────────────────────── */}
          {activeView === 'summary' && (
            <div style={mobileStyles.section}>
              {/* Summary hero card */}
              <div style={mobileStyles.summaryCard}>
                <div style={{ fontSize: '13px', opacity: 0.85, marginBottom: '4px' }}>{formattedDate}</div>
                <div style={{ fontSize: '18px', fontWeight: '700' }}>{selectedShift} Shift</div>
                <div style={mobileStyles.summaryGrid}>
                  <div style={mobileStyles.summaryItem}>
                    <div style={mobileStyles.summaryItemLabel}>Total Sales</div>
                    <div style={mobileStyles.summaryItemValue}>{formatCurrency(totalSales)}</div>
                  </div>
                  <div style={mobileStyles.summaryItem}>
                    <div style={mobileStyles.summaryItemLabel}>Expenses</div>
                    <div style={mobileStyles.summaryItemValue}>{formatCurrency(totalExpenses)}</div>
                  </div>
                  <div style={mobileStyles.summaryItem}>
                    <div style={mobileStyles.summaryItemLabel}>Cash at Hand</div>
                    <div style={{ ...mobileStyles.summaryItemValue, color: cashAtHand >= 0 ? '#bbf7d0' : '#fca5a5' }}>{formatCurrency(cashAtHand)}</div>
                  </div>
                  <div style={mobileStyles.summaryItem}>
                    <div style={mobileStyles.summaryItemLabel}>Total Entries</div>
                    <div style={mobileStyles.summaryItemValue}>{filteredSales.length + filteredExpenses.length}</div>
                  </div>
                </div>
              </div>

              {/* Sales by category */}
              <div style={mobileStyles.card}>
                <div style={mobileStyles.cardHeader}><span style={mobileStyles.cardTitle}>💰 Sales by Category</span></div>
                {Object.entries(salesByCategory).length === 0 ? (
                  <div style={{ padding: '16px', fontSize: '13px', color: '#64748b' }}>No sales recorded</div>
                ) : (
                  Object.entries(salesByCategory).map(([cat, amt]) => (
                    <div key={cat} style={{ ...mobileStyles.listItem, alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#0f172a' }}>{cat}</span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{formatCurrency(amt)}</span>
                    </div>
                  ))
                )}
              </div>

              {/* Expenses by category */}
              <div style={mobileStyles.card}>
                <div style={mobileStyles.cardHeader}><span style={mobileStyles.cardTitle}>📤 Expenses by Category</span></div>
                {Object.entries(expensesByCategory).length === 0 ? (
                  <div style={{ padding: '16px', fontSize: '13px', color: '#64748b' }}>No expenses recorded</div>
                ) : (
                  Object.entries(expensesByCategory).map(([cat, amt]) => (
                    <div key={cat} style={{ ...mobileStyles.listItem, alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#0f172a' }}>{cat}</span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>{formatCurrency(amt)}</span>
                    </div>
                  ))
                )}
              </div>

              {/* Close Shift button */}
              <button
                style={{ ...mobileStyles.dangerBtn, marginTop: '8px' }}
                onClick={() => setShowCloseShiftPrompt(true)}
                disabled={isClosingShift}
              >
                🔒 {isClosingShift ? 'Closing Shift...' : 'Close Shift'}
              </button>
            </div>
          )}
        </div>

        {/* ── FAB: Add Sale / Expense ───────────────────────────────────────── */}
        {(activeView === 'sales' || activeView === 'expenses') && (
          <button
            style={mobileStyles.fab}
            onClick={() => activeView === 'sales' ? setShowMobileAddSale(true) : setShowMobileAddExpense(true)}
          >+</button>
        )}

        {/* ── Bottom Tab Bar ────────────────────────────────────────────────── */}
        <div style={mobileStyles.bottomTabBar}>
          {[
            { id: 'sales', icon: '💰', label: 'Sales' },
            { id: 'expenses', icon: '📤', label: 'Expenses' },
            { id: 'summary', icon: '📊', label: 'Summary' },
            { id: 'history', icon: '📈', label: 'History' },
          ].map(tab => (
            <button
              key={tab.id}
              style={mobileStyles.tabItem(activeView === tab.id)}
              onClick={() => {
                if (tab.id === 'history') handleSalesHistoryClick();
                else setActiveView(tab.id);
              }}
            >
              <span style={mobileStyles.tabIcon(activeView === tab.id)}>{tab.icon}</span>
              <span style={mobileStyles.tabLabel(activeView === tab.id)}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Slide-out Menu ────────────────────────────────────────────────── */}
        {mobileMenuOpen && (
          <div style={mobileStyles.menuOverlay} onClick={() => setMobileMenuOpen(false)}>
            <div style={mobileStyles.menuPanel} onClick={e => e.stopPropagation()}>
              <div style={mobileStyles.menuHeader}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{clinicName}</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>{employeeName}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{selectedShift} Shift · {formattedDate}</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {[
                  { id: 'sales', icon: '💰', label: 'Sales Entry' },
                  { id: 'expenses', icon: '📤', label: 'Expenses' },
                  { id: 'summary', icon: '📊', label: 'Shift Summary' },
                  { id: 'history', icon: '📈', label: 'Sales History' },
                ].map(item => (
                  <button key={item.id} style={mobileStyles.menuItem(activeView === item.id)}
                    onClick={() => { if (item.id === 'history') handleSalesHistoryClick(); else { setActiveView(item.id); setMobileMenuOpen(false); } }}>
                    <span style={mobileStyles.menuItemIcon}>{item.icon}</span>
                    <span style={mobileStyles.menuItemLabel(activeView === item.id)}>{item.label}</span>
                  </button>
                ))}
              </div>
              <div style={{ padding: '16px 20px', borderTop: '1px solid #f1f5f9' }}>
                <button style={mobileStyles.dangerBtn} onClick={() => { setMobileMenuOpen(false); setShowCloseShiftPrompt(true); }}>
                  🔒 Close Shift
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Add Sale Bottom Sheet ─────────────────────────────────────────── */}
        {showMobileAddSale && (
          <div style={mobileStyles.bottomSheetOverlay} onClick={() => setShowMobileAddSale(false)}>
            <div style={mobileStyles.bottomSheet} onClick={e => e.stopPropagation()}>
              <div style={mobileStyles.bottomSheetHandle} />
              <div style={mobileStyles.bottomSheetTitle}>💰 Add Sale</div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Amount (UGX)</label>
                <input type="number" inputMode="numeric" style={mobileStyles.formInput} value={newSale.amount} onChange={e => setNewSale({ ...newSale, amount: e.target.value })} placeholder="0" />
              </div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Reason / Description</label>
                <input type="text" style={mobileStyles.formInput} value={newSale.reason} onChange={e => setNewSale({ ...newSale, reason: e.target.value })} placeholder="Enter reason..." />
              </div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Category</label>
                <select style={mobileStyles.formSelect} value={newSale.category} onChange={e => setNewSale({ ...newSale, category: e.target.value })}>
                  <option value="">Select category...</option>
                  {allSalesCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Served By: <strong>{employee}</strong></div>
              <button style={{ ...mobileStyles.primaryBtn, opacity: isAddingSale ? 0.7 : 1 }} onClick={handleAddSale} disabled={isAddingSale}>
                {isAddingSale ? 'Adding...' : 'Add Sale'}
              </button>
              <button style={mobileStyles.secondaryBtn} onClick={() => setShowMobileAddSale(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Add Expense Bottom Sheet ──────────────────────────────────────── */}
        {showMobileAddExpense && (
          <div style={mobileStyles.bottomSheetOverlay} onClick={() => setShowMobileAddExpense(false)}>
            <div style={mobileStyles.bottomSheet} onClick={e => e.stopPropagation()}>
              <div style={mobileStyles.bottomSheetHandle} />
              <div style={mobileStyles.bottomSheetTitle}>📤 Add Expense</div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Amount (UGX)</label>
                <input type="number" inputMode="numeric" style={mobileStyles.formInput} value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })} placeholder="0" />
              </div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Details</label>
                <input type="text" style={mobileStyles.formInput} value={newExpense.details} onChange={e => setNewExpense({ ...newExpense, details: e.target.value })} placeholder="What was the expense for?" />
              </div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Category</label>
                <select style={mobileStyles.formSelect} value={newExpense.category} onChange={e => setNewExpense({ ...newExpense, category: e.target.value })}>
                  <option value="">Select category...</option>
                  {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={mobileStyles.formGroup}>
                <label style={mobileStyles.formLabel}>Taken By</label>
                <input type="text" style={mobileStyles.formInput} value={newExpense.takenBy} onChange={e => setNewExpense({ ...newExpense, takenBy: e.target.value })} placeholder="Who took the money?" />
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Served By: <strong>{employee}</strong></div>
              <button style={{ ...mobileStyles.primaryBtn, opacity: isAddingExpense ? 0.7 : 1 }} onClick={handleAddExpense} disabled={isAddingExpense}>
                {isAddingExpense ? 'Adding...' : 'Add Expense'}
              </button>
              <button style={mobileStyles.secondaryBtn} onClick={() => setShowMobileAddExpense(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Shared modals and animation */}
        {renderConfirmationModals()}
        {renderCloseShiftAnimation()}

        <ToastContainer position="top-center" autoClose={4000} hideProgressBar newestOnTop closeOnClick theme="light" toastStyle={{ borderRadius: '12px', fontSize: '13px', margin: '8px' }} />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ─── DESKTOP LAYOUT (original design preserved) ───────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════


  const navSections = [
    { label: 'SALES', items: [{ id: 'sales', icon: '💰', label: 'Sales Entry' }, { id: 'expenses', icon: '📤', label: 'Expenses' }] },
    { label: 'REPORTS', items: [{ id: 'history', icon: '📊', label: 'Sales History' }, { id: 'summary', icon: '📈', label: 'Shift Summary' }] },
  ];

  const renderSalesFilters = () => (
    <div style={{ padding: sidebarCollapsed ? '8px 0' : '16px 14px', borderBottom: `1px solid ${theme.sidebarBorder}`, display: sidebarCollapsed ? 'none' : 'block' }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textPrimary, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        🔍 Filter Sales
      </div>
      <div style={{ marginBottom: '12px' }}>
        {[['servedBy', 'By Names'], ['category', 'By Category']].map(([val, label]) => (
          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textSecondary, marginBottom: '8px', cursor: 'pointer' }}>
            <input type="radio" value={val} checked={filterType === val} onChange={() => setFilterType(val)} />
            {label}
          </label>
        ))}
      </div>
      <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
        {(filterType === 'servedBy' ? availableServedBy : availableCategories).map(item => (
          <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontSize: '12px', color: effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textSecondary, cursor: 'pointer', borderRadius: '4px' }}>
            <input type="checkbox"
              checked={filterType === 'servedBy' ? selectedServedByFilters.includes(item) : selectedCategoryFilters.includes(item)}
              onChange={(e) => filterType === 'servedBy'
                ? setSelectedServedByFilters(prev => e.target.checked ? [...prev, item] : prev.filter(x => x !== item))
                : setSelectedCategoryFilters(prev => e.target.checked ? [...prev, item] : prev.filter(x => x !== item))}
            />
            <span style={{ fontSize: '11px' }}>{item}</span>
          </label>
        ))}
      </div>
      <div>
        <button style={{ padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', border: 'none', background: theme.activeNavBg, color: effectiveTheme === 'blue' ? '#fff' : theme.textPrimary, cursor: 'pointer', marginRight: '6px' }}
          onClick={() => filterType === 'servedBy' ? setSelectedServedByFilters([...availableServedBy]) : setSelectedCategoryFilters([...availableCategories])}>Select All</button>
        <button style={{ padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', border: 'none', background: theme.danger, color: '#fff', cursor: 'pointer' }}
          onClick={() => { setSelectedServedByFilters([]); setSelectedCategoryFilters([]); }}>Clear</button>
      </div>
    </div>
  );

  const renderExpenseFilters = () => (
    <div style={{ padding: sidebarCollapsed ? '8px 0' : '16px 14px', borderBottom: `1px solid ${theme.sidebarBorder}`, display: sidebarCollapsed ? 'none' : 'block' }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textPrimary, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        🔍 Filter Expenses
      </div>
      <div style={{ marginBottom: '12px' }}>
        {[['takenBy', 'By Taken By'], ['category', 'By Category']].map(([val, label]) => (
          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textSecondary, marginBottom: '8px', cursor: 'pointer' }}>
            <input type="radio" value={val} checked={expenseFilterType === val} onChange={() => setExpenseFilterType(val)} />
            {label}
          </label>
        ))}
      </div>
      <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
        {(expenseFilterType === 'takenBy' ? availableExpenseTakenBy : availableExpenseCategories).map(item => (
          <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontSize: '12px', color: effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textSecondary, cursor: 'pointer', borderRadius: '4px' }}>
            <input type="checkbox"
              checked={expenseFilterType === 'takenBy' ? selectedExpenseTakenByFilters.includes(item) : selectedExpenseCategoryFilters.includes(item)}
              onChange={(e) => expenseFilterType === 'takenBy'
                ? setSelectedExpenseTakenByFilters(prev => e.target.checked ? [...prev, item] : prev.filter(x => x !== item))
                : setSelectedExpenseCategoryFilters(prev => e.target.checked ? [...prev, item] : prev.filter(x => x !== item))}
            />
            <span style={{ fontSize: '11px' }}>{item}</span>
          </label>
        ))}
      </div>
      <div>
        <button style={{ padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', border: 'none', background: theme.activeNavBg, color: effectiveTheme === 'blue' ? '#fff' : theme.textPrimary, cursor: 'pointer', marginRight: '6px' }}
          onClick={() => expenseFilterType === 'takenBy' ? setSelectedExpenseTakenByFilters([...availableExpenseTakenBy]) : setSelectedExpenseCategoryFilters([...availableExpenseCategories])}>Select All</button>
        <button style={{ padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', border: 'none', background: theme.danger, color: '#fff', cursor: 'pointer' }}
          onClick={() => { setSelectedExpenseTakenByFilters([]); setSelectedExpenseCategoryFilters([]); }}>Clear</button>
      </div>
    </div>
  );

  const renderShiftSummary = () => {
    const salesByCategory = {};
    getFilteredSales().forEach(s => { const c = s.category || 'non-categorized'; salesByCategory[c] = (salesByCategory[c] || 0) + s.amount; });
    const expensesByCategory = {};
    getFilteredExpenses().forEach(e => { const c = e.category || 'non-categorized'; expensesByCategory[c] = (expensesByCategory[c] || 0) + e.amount; });
    return (
      <div style={{ ...desktopStyles.card, marginTop: '16px' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: theme.textPrimary, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>📊</span> Shift Summary - {formattedDate} ({selectedShift} Shift)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '20px', background: `linear-gradient(135deg, ${theme.sidebarBg} 0%, ${theme.activeNavBg} 100%)`, borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
          {[['Total Sales', formatCurrency(totalSales)], ['Total Expenses', formatCurrency(totalExpenses)], ['Cash at Hand', formatCurrency(cashAtHand)], ['Net Entries', getFilteredSales().length + getFilteredExpenses().length]].map(([label, value]) => (
            <div key={label} style={{ background: effectiveTheme === 'blue' ? 'rgba(255,255,255,0.1)' : theme.tableHeader, borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px', color: effectiveTheme === 'blue' ? '#fff' : theme.textSecondary }}>{label}</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: effectiveTheme === 'blue' ? '#fff' : theme.textPrimary }}>{value}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {[['💰 Sales by Category', salesByCategory, 'No sales recorded'], ['📤 Expenses by Category', expensesByCategory, 'No expenses recorded']].map(([title, data, empty]) => (
            <div key={title}>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: theme.textPrimary }}>{title}</div>
              {Object.keys(data).length === 0 ? <div style={{ color: theme.textMuted, fontSize: '13px' }}>{empty}</div> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                  {Object.entries(data).map(([cat, amt]) => (
                    <div key={cat} style={{ background: effectiveTheme === 'blue' ? 'rgba(255,255,255,0.05)' : theme.tableHeader, borderRadius: '8px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: theme.textPrimary }}>{cat}</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: theme.textPrimary }}>{formatCurrency(amt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button onClick={() => setShowCloseShiftPrompt(true)} disabled={isClosingShift}
            style={{ padding: '14px 32px', background: isClosingShift ? theme.textMuted : theme.danger, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '700', cursor: isClosingShift ? 'not-allowed' : 'pointer', minWidth: '160px', height: '52px' }}>
            {isClosingShift ? 'Closing Shift...' : 'Close Shift'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif", background: theme.pureWhite, position: 'relative' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${effectiveTheme === 'blue' ? '#1e293b' : '#f1f5f9'}; }
        ::-webkit-scrollbar-thumb { background: ${effectiveTheme === 'blue' ? '#475569' : '#cbd5e1'}; border-radius: 4px; }
        .nav-item:hover { background: ${theme.navHoverBg} !important; }
        .table-row:hover td { background: ${theme.tableRowHover}; }
        .action-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .fade-in { animation: fadeIn 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        table { border-collapse: collapse; width: 100%; }
        th:last-child, td:last-child { border-right: none; }
        .collapse-btn:hover { background: ${theme.collapseButtonHover} !important; transform: scale(1.05); }
      `}</style>

      <div style={{ width: '100%' }}>
        <Topbar token={tokenFromUrl} themeColor={effectiveTheme} />
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0, marginTop: '64px' }}>
        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <aside style={{ width: sidebarCollapsed ? '80px' : '280px', background: theme.sidebarBg, display: 'flex', flexDirection: 'column', flexShrink: 0, borderRight: `1px solid ${theme.sidebarBorder}`, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', transition: 'width 0.3s ease' }}>
          <div style={{ padding: sidebarCollapsed ? '20px 10px' : '20px 16px', borderBottom: `2px solid ${theme.sidebarBorder}`, display: 'flex', flexDirection: sidebarCollapsed ? 'column' : 'row', alignItems: 'center', justifyContent: 'space-between', background: effectiveTheme === 'blue' ? 'rgba(0,0,0,0.2)' : theme.filterSection, minHeight: sidebarCollapsed ? '120px' : '80px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, order: sidebarCollapsed ? 2 : 1, marginTop: sidebarCollapsed ? '12px' : 0 }}>
              <div style={{ width: '45px', height: '45px', background: theme.activeNavBg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0, color: effectiveTheme === 'blue' ? '#ffffff' : theme.textPrimary, fontWeight: 'bold' }}>CP</div>
              {!sidebarCollapsed && (
                <div>
                  <div style={{ color: effectiveTheme === 'blue' ? '#ffffff' : theme.textPrimary, fontWeight: '700', fontSize: '16px' }}>SmartShop</div>
                  <div style={{ color: theme.sectionHeaderText, fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>Sales Management</div>
                </div>
              )}
            </div>
            <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{ background: theme.collapseButtonBg, border: 'none', color: theme.collapseButtonText, width: '42px', height: '42px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '20px', fontWeight: 'bold', transition: 'all 0.2s ease', order: sidebarCollapsed ? 1 : 2, marginLeft: sidebarCollapsed ? 0 : 'auto', flexShrink: 0 }}>
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>

          <nav style={{ padding: sidebarCollapsed ? '16px 0' : '16px 12px', borderBottom: `1px solid ${theme.sidebarBorder}` }}>
            {navSections.map(section => (
              <div key={section.label} style={{ marginBottom: '4px' }}>
                {!sidebarCollapsed && <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: theme.sectionHeaderText, padding: '0 14px', marginBottom: '8px', marginTop: '12px' }}>{section.label}</div>}
                {section.items.map(item => (
                  <button key={item.id}
                    className={`nav-item ${activeView === item.id ? 'active-tab' : ''}`}
                    onClick={() => { if (item.id === 'history') handleSalesHistoryClick(); else setActiveView(item.id); }}
                    onMouseEnter={() => setHoveredNavItem(item.id)}
                    onMouseLeave={() => setHoveredNavItem(null)}
                    style={{ ...desktopStyles.navItem(activeView === item.id, sidebarCollapsed), padding: sidebarCollapsed ? '12px 0' : '10px 14px', color: activeView === item.id ? theme.activeNavText : (effectiveTheme === 'blue' ? theme.inactiveNavText : theme.textSecondary) }}>
                    <span style={{ fontSize: '18px', width: '20px', textAlign: 'center' }}>{item.icon}</span>
                    {!sidebarCollapsed && <span style={{ fontWeight: '600' }}>{item.label}</span>}
                    {sidebarCollapsed && hoveredNavItem === item.id && (
                      <div style={{ position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px', padding: '8px 12px', background: theme.tooltipBg, color: theme.tooltipText, fontSize: '12px', fontWeight: '500', borderRadius: '6px', whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>{item.label}</div>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {activeView === 'sales' && renderSalesFilters()}
            {activeView === 'expenses' && renderExpenseFilters()}
          </div>

          <div style={{ padding: sidebarCollapsed ? '16px 0' : '16px 16px 20px', borderTop: `1px solid ${theme.sidebarBorder}`, background: effectiveTheme === 'blue' ? 'rgba(0,0,0,0.2)' : theme.filterSection, textAlign: sidebarCollapsed ? 'center' : 'left' }}>
            {!sidebarCollapsed ? (
              <>
                <div style={{ fontSize: '11px', color: theme.sectionHeaderText, marginBottom: '4px', fontWeight: '600', letterSpacing: '0.05em' }}>{clinicName}</div>
                <div style={{ fontSize: '12px', color: effectiveTheme === 'blue' ? '#ffffff' : theme.textPrimary, fontWeight: '500' }}>{employeeName}</div>
                <div style={{ fontSize: '10px', color: theme.sectionHeaderText, marginTop: '4px' }}>{selectedShift} Shift</div>
              </>
            ) : <span style={{ fontSize: '18px' }}>👤</span>}
          </div>
        </aside>

        {/* ── Main Content ─────────────────────────────────────────────────── */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', background: theme.mainBg, borderRadius: '24px 0 0 0', margin: '16px 16px 16px 0', boxShadow: effectiveTheme === 'blue' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 4px 12px rgba(0,0,0,0.05)', transition: 'margin-left 0.3s ease' }}>
          {/* Sticky Header */}
          <div style={desktopStyles.stickyHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '30px', fontSize: '13px', fontWeight: '500', color: theme.textSecondary }}>
              <span>📅</span><span>{formattedDate} · {selectedShift} Shift</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '600', background: theme.badgeBlue.bg, color: theme.badgeBlue.text }}>
                {activeView === 'sales' ? getFilteredSales().length : getFilteredExpenses().length} entries
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {[['Total Sales', formatNumber(totalSales), theme.info], ['Expenses', formatNumber(totalExpenses), theme.danger], ['Cash at Hand', formatNumber(cashAtHand), cashAtHand >= 0 ? theme.accent : theme.danger]].map(([label, value, color]) => (
                <div key={label} style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '10px', padding: '12px 20px', minWidth: '140px' }}>
                  <div style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '4px' }}>{label}</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color }}>{value}</div>
                </div>
              ))}
              <button onClick={() => setShowCloseShiftPrompt(true)} disabled={isClosingShift}
                style={{ background: theme.danger, color: '#ffffff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: isClosingShift ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', height: '40px', whiteSpace: 'nowrap' }}>
                <span>🔒</span>{isClosingShift ? 'Closing...' : 'Close Shift'}
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 28px 24px 28px' }} className="fade-in">
            {activeView === 'summary' ? renderShiftSummary() : activeView === 'sales' ? (
              <div style={desktopStyles.tableWrapper}>
                <table>
                  <thead>
                    <tr>
                      {['Amount (UGX)', 'Reason', 'Category', 'Served By', 'Actions'].map(h => <th key={h} style={desktopStyles.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredSales().map((sale, index) => (
                      <tr key={sale.id || index} className="table-row">
                        <td style={{ ...desktopStyles.td, fontWeight: '700' }}>{formatCurrency(sale.amount)}</td>
                        <td style={desktopStyles.td}>{sale.reason}</td>
                        <td style={desktopStyles.td}><span style={desktopStyles.badge('gray')}>{sale.category || 'non-categorized'}</span></td>
                        <td style={desktopStyles.td}>{sale.servedBy}</td>
                        <td style={{ ...desktopStyles.td, textAlign: 'center', padding: '13px 8px' }}>
                          {index !== 0 ? (
                            <button className="action-btn" onClick={() => handleDeleteSale(sale.id)} disabled={deletingSaleId === sale.id}
                              style={{ ...desktopStyles.actionButton, background: theme.danger, color: '#fff', opacity: deletingSaleId === sale.id ? 0.6 : 1, margin: '0 auto' }}>
                              {deletingSaleId === sale.id ? '...' : 'Delete'}
                            </button>
                          ) : <button style={{ ...desktopStyles.actionButton, background: theme.textMuted, color: theme.cardBg, cursor: 'not-allowed', opacity: 0.8, margin: '0 auto' }} disabled>Delete</button>}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: theme.tableHeader }}>
                      <td style={desktopStyles.td}><input type="text" style={{ ...desktopStyles.input, width: '100px' }} value={newSale.amount} onChange={e => setNewSale({ ...newSale, amount: e.target.value })} placeholder="Amount" /></td>
                      <td style={desktopStyles.td}><input type="text" style={{ ...desktopStyles.input, width: '150px' }} value={newSale.reason} onChange={e => setNewSale({ ...newSale, reason: e.target.value })} placeholder="Reason" /></td>
                      <td style={desktopStyles.td}>
                        <select style={{ ...desktopStyles.input, width: '130px' }} value={newSale.category} onChange={e => setNewSale({ ...newSale, category: e.target.value })}>
                          <option value="">Select Category</option>
                          {allSalesCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={desktopStyles.td}><span style={{ fontWeight: '600', color: theme.textSecondary }}>{employee}</span></td>
                      <td style={{ ...desktopStyles.td, textAlign: 'center', padding: '13px 8px' }}>
                        <button className="action-btn" onClick={handleAddSale} disabled={isAddingSale}
                          style={{ ...desktopStyles.actionButton, background: theme.accent, color: '#fff', width: '60px', cursor: isAddingSale ? 'not-allowed' : 'pointer', opacity: isAddingSale ? 0.6 : 1, margin: '0 auto' }}>
                          {isAddingSale ? '...' : 'Add'}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : activeView === 'expenses' ? (
              <div style={desktopStyles.tableWrapper}>
                <table>
                  <thead>
                    <tr>
                      {['Amount (UGX)', 'Details', 'Category', 'Taken By', 'Served By', 'Actions'].map(h => <th key={h} style={desktopStyles.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredExpenses().map((expense, index) => (
                      <tr key={expense.id || index} className="table-row">
                        <td style={{ ...desktopStyles.td, fontWeight: '700' }}>{formatCurrency(expense.amount)}</td>
                        <td style={desktopStyles.td}>{expense.details}</td>
                        <td style={desktopStyles.td}><span style={desktopStyles.badge('gray')}>{expense.category}</span></td>
                        <td style={desktopStyles.td}>{expense.takenBy}</td>
                        <td style={desktopStyles.td}>{expense.servedBy}</td>
                        <td style={{ ...desktopStyles.td, textAlign: 'center', padding: '13px 8px' }}>
                          {index !== 0 ? (
                            <button className="action-btn" onClick={() => handleDeleteExpense(expense.id)} disabled={deletingExpenseId === expense.id}
                              style={{ ...desktopStyles.actionButton, background: theme.danger, color: '#fff', opacity: deletingExpenseId === expense.id ? 0.6 : 1, margin: '0 auto' }}>
                              {deletingExpenseId === expense.id ? '...' : 'Delete'}
                            </button>
                          ) : <button style={{ ...desktopStyles.actionButton, background: theme.textMuted, color: theme.cardBg, cursor: 'not-allowed', opacity: 0.8, margin: '0 auto' }} disabled>Delete</button>}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: theme.tableHeader }}>
                      <td style={desktopStyles.td}><input type="text" style={{ ...desktopStyles.input, width: '100px' }} value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })} placeholder="Amount" /></td>
                      <td style={desktopStyles.td}><input type="text" style={{ ...desktopStyles.input, width: '150px' }} value={newExpense.details} onChange={e => setNewExpense({ ...newExpense, details: e.target.value })} placeholder="Details" /></td>
                      <td style={desktopStyles.td}>
                        <select style={{ ...desktopStyles.input, width: '130px' }} value={newExpense.category} onChange={e => setNewExpense({ ...newExpense, category: e.target.value })}>
                          <option value="">Select Category</option>
                          {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={desktopStyles.td}><input type="text" style={{ ...desktopStyles.input, width: '100px' }} value={newExpense.takenBy} onChange={e => setNewExpense({ ...newExpense, takenBy: e.target.value })} placeholder="Taken By" /></td>
                      <td style={desktopStyles.td}><span style={{ fontWeight: '600', color: theme.textSecondary }}>{employee}</span></td>
                      <td style={{ ...desktopStyles.td, textAlign: 'center', padding: '13px 8px' }}>
                        <button className="action-btn" onClick={handleAddExpense} disabled={isAddingExpense}
                          style={{ ...desktopStyles.actionButton, background: theme.accent, color: '#fff', width: '60px', cursor: isAddingExpense ? 'not-allowed' : 'pointer', opacity: isAddingExpense ? 0.6 : 1, margin: '0 auto' }}>
                          {isAddingExpense ? '...' : 'Add'}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {renderConfirmationModals()}
      {renderCloseShiftAnimation()}

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick theme={effectiveTheme === 'blue' ? 'dark' : 'light'} />
    </div>
  );
};

export default Sales;